import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Send, Mail, Users, Loader2, AlertTriangle, FileCheck, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin modal to preview + send the "Conference Postponed to Mar 2027" announcement.
 *
 * Two-stage flow:
 *   1. Load preview HTML + audience counts from GET /api/admin/campaigns/postponement/preview
 *   2. Admin picks audience, clicks Send → POST /api/admin/campaigns/postponement/send
 *   3. Optionally trigger POST /api/admin/registrations/reissue-invitations-all to
 *      regenerate visa letters with the new dates for all paid/exempted attendees.
 */
export default function PostponementCampaignModal({ open, onClose, token, language = 'fr' }) {
  const t = language === 'fr' ? {
    title: 'Annonce : Conférence reportée',
    subtitle: 'Prévisualisez le message ci-dessous avant de l’envoyer.',
    audience: 'Destinataires',
    audienceAll: 'Tous les inscrits',
    audiencePaid: 'Payés uniquement',
    audienceApproved: 'Approuvés (non payés)',
    audienceUnpaid: 'Non payés (en attente + approuvés + incomplets)',
    preview: 'Aperçu du courriel',
    reload: 'Recharger l’aperçu',
    subject: 'Sujet',
    send: 'Envoyer l’annonce',
    sending: 'Envoi en cours…',
    reissue: 'Réémettre les lettres d’invitation (paid + exemptés)',
    reissuing: 'Réémission des lettres…',
    close: 'Fermer',
    resultSent: (n) => `${n} courriels envoyés.`,
    resultReissued: (n, tot) => `${n}/${tot} lettres d’invitation réémises.`,
    confirmSend: 'Confirmez-vous l’envoi de cette annonce à tous les destinataires sélectionnés ?',
    confirmReissue: 'Cela va révoquer les anciennes lettres et en générer de nouvelles pour tous les participants payés et exemptés. Continuer ?',
  } : {
    title: 'Announcement: Conference Postponed',
    subtitle: 'Preview the message below before sending.',
    audience: 'Audience',
    audienceAll: 'All registered users',
    audiencePaid: 'Paid only',
    audienceApproved: 'Approved (not yet paid)',
    audienceUnpaid: 'Unpaid (pending + approved + incomplete)',
    preview: 'Email preview',
    reload: 'Reload preview',
    subject: 'Subject',
    send: 'Send announcement',
    sending: 'Sending…',
    reissue: 'Reissue visa invitation letters (paid + exempted)',
    reissuing: 'Reissuing letters…',
    close: 'Close',
    resultSent: (n) => `${n} emails sent.`,
    resultReissued: (n, tot) => `${n}/${tot} invitation letters reissued.`,
    confirmSend: 'Are you sure you want to send this announcement to all selected recipients?',
    confirmReissue: 'This will revoke old invitation letters and generate new ones for all paid and exempted attendees. Continue?',
  };

  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [audience, setAudience] = useState('all');
  const [sending, setSending] = useState(false);
  const [reissuing, setReissuing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(null); // { kind, sent|reissued, total, status }

  useEffect(() => {
    if (!open) return;
    loadPreview();
    setResult(null);
    setError('');
    setProgress(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pollJob = async (job_id, kind) => {
    // Poll every 2s until job completes/fails.
    let done = false;
    for (let i = 0; i < 600 && !done; i += 1) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const { data } = await axios.get(
          `${BACKEND_URL}/api/admin/jobs/${job_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setProgress({ kind, ...data });
        if (data.status === 'completed' || data.status === 'failed') {
          done = true;
          setResult({ kind, ...data });
          if (data.status === 'failed') setError(data.error || 'Job failed');
        }
      } catch (err) {
        // Ignore transient errors; keep polling.
      }
    }
  };

  const loadPreview = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/admin/campaigns/postponement/preview`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreview(data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!window.confirm(t.confirmSend)) return;
    setSending(true);
    setError('');
    setProgress(null);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/campaigns/postponement/send`,
        { audience },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Backend returns immediately with job_id — poll for progress.
      if (data?.job_id) {
        setProgress({ kind: 'sent', status: 'queued', sent: 0, total: 0, job_id: data.job_id });
        pollJob(data.job_id, 'sent');
      } else {
        setResult({ kind: 'sent', ...data });
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const handleReissue = async () => {
    if (!window.confirm(t.confirmReissue)) return;
    setReissuing(true);
    setError('');
    setProgress(null);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/registrations/reissue-invitations-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data?.job_id) {
        setProgress({ kind: 'reissued', status: 'queued', reissued: 0, total: 0, job_id: data.job_id });
        pollJob(data.job_id, 'reissued');
      } else {
        setResult({ kind: 'reissued', ...data });
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Reissue failed');
    } finally {
      setReissuing(false);
    }
  };

  if (!open) return null;

  const counts = preview?.audience_counts || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="postponement-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#CD0000] to-[#D4AF37] flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <h3 className="font-extrabold text-[#1D1D1D]">{t.title}</h3>
              <p className="text-xs text-gray-500">{t.subtitle}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 overflow-y-auto space-y-5 flex-1">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg">{error}</div>
          )}

          {loading || !preview ? (
            <div className="py-12 flex items-center justify-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <>
              {/* Audience picker */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">{t.audience}</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {[
                    ['all', t.audienceAll, counts.all],
                    ['paid', t.audiencePaid, counts.paid],
                    ['approved', t.audienceApproved, counts.approved],
                    ['unpaid', t.audienceUnpaid, counts.unpaid],
                  ].map(([k, label, n]) => (
                    <label key={k} className={`flex items-center justify-between gap-3 border rounded-xl px-3 py-2 cursor-pointer transition ${
                      audience === k ? 'border-[#CD0000] bg-red-50' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="postponement-audience"
                          value={k}
                          checked={audience === k}
                          onChange={() => setAudience(k)}
                          data-testid={`postponement-audience-${k}`}
                          className="accent-[#CD0000]"
                        />
                        <span className="text-sm font-semibold text-[#1D1D1D]">{label}</span>
                      </div>
                      <span className="text-xs font-bold text-gray-500 bg-white border border-gray-200 rounded-md px-2 py-0.5">
                        <Users className="w-3 h-3 inline mr-1" /> {n ?? '–'}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Preview */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">{t.preview}</label>
                  <button onClick={loadPreview} className="text-xs font-bold text-gray-500 hover:text-[#CD0000] flex items-center gap-1">
                    <RefreshCw className="w-3.5 h-3.5" /> {t.reload}
                  </button>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
                  <p className="text-xs text-gray-500 mb-2">{t.subject}: <span className="font-bold text-[#1D1D1D]">{preview.subject}</span></p>
                  <iframe
                    data-testid="postponement-preview-iframe"
                    title="Postponement email preview"
                    srcDoc={preview.html}
                    className="w-full h-[420px] rounded-lg bg-white border border-gray-100"
                  />
                </div>
              </div>

              {/* Progress (background job running) */}
              {progress && !result && (
                <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm px-3 py-3 rounded-lg" data-testid="postponement-progress">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="font-semibold">
                      {progress.kind === 'sent'
                        ? `Sending emails — ${progress.sent || 0} / ${progress.total || '?'}`
                        : `Reissuing invitation letters — ${progress.reissued || 0} / ${progress.total || '?'}`}
                    </span>
                  </div>
                  <p className="text-xs mt-1 opacity-80">Status: {progress.status || 'queued'}. This runs in the background — you can safely close this modal.</p>
                </div>
              )}

              {/* Result */}
              {/* Result */}
              {result && (
                <div className={`border text-sm px-3 py-3 rounded-lg ${result.status === 'failed' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`}>
                  {result.kind === 'sent' && t.resultSent(result.sent || 0)}
                  {result.kind === 'reissued' && t.resultReissued(result.reissued || 0, result.total || 0)}
                  {Array.isArray(result.errors) && result.errors.length > 0 && (
                    <details className="mt-2 text-xs opacity-80">
                      <summary className="cursor-pointer">Errors ({result.errors.length})</summary>
                      <pre className="mt-2 whitespace-pre-wrap">{JSON.stringify(result.errors, null, 2)}</pre>
                    </details>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex flex-wrap gap-2 justify-end shrink-0">
          <button onClick={onClose} className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-4 py-2 rounded-lg text-sm">{t.close}</button>
          <button
            onClick={handleReissue}
            disabled={reissuing || sending}
            data-testid="postponement-reissue-btn"
            className="bg-[#D4AF37] hover:bg-amber-500 text-black font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60"
          >
            {reissuing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileCheck className="w-4 h-4" />}
            {reissuing ? t.reissuing : t.reissue}
          </button>
          <button
            onClick={handleSend}
            disabled={sending || reissuing || !preview}
            data-testid="postponement-send-btn"
            className="bg-[#CD0000] hover:bg-[#A00000] text-white font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? t.sending : t.send}
          </button>
        </div>
      </div>
    </div>
  );
}
